import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Form, Row, Col } from 'react-bootstrap';
import type { FormValues } from '../CustomerWizard';
import {
  LEGAL_ENTITY_TYPES,
  getLegalEntityType,
  registrationLabelFor,
  registrationPlaceholderFor,
} from '../../../../utils/validation/legalEntityType';

const CompanyInfoStep: React.FC = () => {
  const { register, formState: { errors }, setValue, trigger } = useFormContext<FormValues>();
  const legalEntityType = useWatch({ name: 'partyInfo.legalEntityType' }) as string | undefined;
  const selectedLegalEntityType = getLegalEntityType(legalEntityType);
  
  // Watch directors and representativeOccupation
  const directors = useWatch({ name: 'partyInfo.directors' }) || [];
  const representativeOccupation = useWatch({ name: 'partyInfo.representativeOccupation' });
  
  // Auto-populate representativeOccupation from first director's designation if empty
  useEffect(() => {
    if (!representativeOccupation && directors.length > 0 && directors[0]?.designation) {
      setValue('partyInfo.representativeOccupation', directors[0].designation, { shouldValidate: false });
    }
  }, [directors, representativeOccupation, setValue]);

  return (
    <div className="company-info-step">
      <h4 className="mb-4">Company Information</h4>
      <p className="text-muted mb-4">
        Enter the company's details. All fields marked with * are required for processing.
      </p>

      {/* Company Details */}
      <div className="form-section mb-4">
        <h6 className="form-section-title mb-3">Company Details</h6>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Registered Name *</Form.Label>
              <Form.Control
                type="text"
                {...register('partyInfo.registeredName')}
                className={errors.partyInfo?.registeredName ? 'is-invalid' : ''}
                placeholder="Enter registered company name"
              />
              {errors.partyInfo?.registeredName && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.registeredName.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          {/* Legal form first: it decides what a valid registration number is. */}
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Legal Entity Type *</Form.Label>
              <Form.Select
                {...register('partyInfo.legalEntityType', {
                  onChange: () => { trigger('partyInfo.registrationNumber'); },
                })}
                className={errors.partyInfo?.legalEntityType ? 'is-invalid' : ''}
              >
                <option value="">Select entity type...</option>
                {LEGAL_ENTITY_TYPES.map((entityType) => (
                  <option key={entityType.code} value={entityType.code}>
                    {entityType.label}
                  </option>
                ))}
              </Form.Select>
              {errors.partyInfo?.legalEntityType ? (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.legalEntityType.message}
                </Form.Control.Feedback>
              ) : (
                selectedLegalEntityType?.hint && (
                  <Form.Text className="text-muted">{selectedLegalEntityType.hint}</Form.Text>
                )
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Trading Name</Form.Label>
              <Form.Control
                type="text"
                {...register('partyInfo.tradingName')}
                className={errors.partyInfo?.tradingName ? 'is-invalid' : ''}
                placeholder="Enter trading name if different"
              />
              {errors.partyInfo?.tradingName && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.tradingName.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>{registrationLabelFor(legalEntityType)} *</Form.Label>
              <Form.Control
                type="text"
                {...register('partyInfo.registrationNumber')}
                className={errors.partyInfo?.registrationNumber ? 'is-invalid' : ''}
                placeholder={registrationPlaceholderFor(legalEntityType)}
              />
              {errors.partyInfo?.registrationNumber && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.registrationNumber.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Tax Number</Form.Label>
              <Form.Control
                type="text"
                {...register('partyInfo.taxNumber')}
                className={errors.partyInfo?.taxNumber ? 'is-invalid' : ''}
                placeholder="Enter tax number"
              />
              {errors.partyInfo?.taxNumber && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.taxNumber.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>VAT Number</Form.Label>
              <Form.Control
                type="text"
                {...register('partyInfo.vatNumber')}
                className={errors.partyInfo?.vatNumber ? 'is-invalid' : ''}
                placeholder="Enter VAT number if applicable"
              />
              {errors.partyInfo?.vatNumber && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.vatNumber.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Tax PIN</Form.Label>
              <Form.Control
                type="text"
                {...register('partyInfo.taxPin')}
                className={errors.partyInfo?.taxPin ? 'is-invalid' : ''}
                placeholder="Enter Tax Personal Identification Number"
              />
              {errors.partyInfo?.taxPin && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.taxPin.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Establishment Date</Form.Label>
              <Form.Control
                type="date"
                {...register('partyInfo.establishmentDate')}
                className={errors.partyInfo?.establishmentDate ? 'is-invalid' : ''}
              />
              {errors.partyInfo?.establishmentDate && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.establishmentDate.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Additional Information */}
      <div className="form-section mb-4">
        <h6 className="form-section-title mb-3">Additional Information</h6>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Representative Occupation</Form.Label>
              <Form.Control
                type="text"
                {...register('partyInfo.representativeOccupation')}
                className={errors.partyInfo?.representativeOccupation ? 'is-invalid' : ''}
                placeholder="Enter occupation"
              />
              {errors.partyInfo?.representativeOccupation && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.representativeOccupation.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Annual Turnover (R)</Form.Label>
              <Form.Control
                type="number"
                {...register('partyInfo.annualTurnover', { valueAsNumber: true })}
                className={errors.partyInfo?.annualTurnover ? 'is-invalid' : ''}
                placeholder="Enter annual turnover"
              />
              {errors.partyInfo?.annualTurnover && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.annualTurnover.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Industry Sector</Form.Label>
              <Form.Select
                {...register('partyInfo.industrySector')}
                className={errors.partyInfo?.industrySector ? 'is-invalid' : ''}
              >
                <option value="">Select industry sector</option>
                <option value="TECHNOLOGY">Technology</option>
                <option value="FINANCE">Financial Services</option>
                <option value="HEALTHCARE">Healthcare</option>
                <option value="CONSTRUCTION">Construction</option>
                <option value="MANUFACTURING">Manufacturing</option>
                <option value="RETAIL">Retail</option>
                <option value="ENERGY">Energy</option>
                <option value="AGRICULTURE">Agriculture</option>
                <option value="LOGISTICS">Logistics and Transportation</option>
                <option value="OTHER">Other</option>
              </Form.Select>
              {errors.partyInfo?.industrySector && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.industrySector.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Source Of Income</Form.Label>
              <Form.Select
                {...register('partyInfo.incomeType')}
                className={errors.partyInfo?.incomeType ? 'is-invalid' : ''}
              >
                <option value="">Select source of income</option>
                <option value="PO">Purchase Orders (PO)</option>
                <option value="TENDER">Tender</option>
                <option value="PRODUCTS_AND_SERVICES">Products and Services</option>
                <option value="OTHER">Other</option>
              </Form.Select>
              {errors.partyInfo?.incomeType && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.incomeType.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Industry Category</Form.Label>
              <Form.Select
                {...register('partyInfo.industryCategory')}
                className={errors.partyInfo?.industryCategory ? 'is-invalid' : ''}
              >
                <option value="">Select industry category</option>
                <option value="GOVERNMENT">Government</option>
                <option value="PRIVATE_SECTOR">Private Sector</option>
                <option value="HOUSEHOLD">Household</option>
                <option value="PERSONAL">Personal</option>
                <option value="OTHER">Other</option>
              </Form.Select>
              {errors.partyInfo?.industryCategory && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.industryCategory.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Employee Count</Form.Label>
              <Form.Control
                type="number"
                {...register('partyInfo.employeeCount', { valueAsNumber: true })}
                className={errors.partyInfo?.employeeCount ? 'is-invalid' : ''}
                placeholder="Enter number of employees"
              />
              {errors.partyInfo?.employeeCount && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.employeeCount.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Business Activity</Form.Label>
              <Form.Control
                type="text"
                {...register('partyInfo.businessActivity')}
                className={errors.partyInfo?.businessActivity ? 'is-invalid' : ''}
                placeholder="Enter business activity"
              />
              {errors.partyInfo?.businessActivity && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.businessActivity.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Income Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                {...register('partyInfo.incomeDescription')}
                className={errors.partyInfo?.incomeDescription ? 'is-invalid' : ''}
                placeholder="Describe the income source"
              />
              {errors.partyInfo?.incomeDescription && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.incomeDescription.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Industry Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                {...register('partyInfo.industryDescription')}
                className={errors.partyInfo?.industryDescription ? 'is-invalid' : ''}
                placeholder="Describe the industry category"
              />
              {errors.partyInfo?.industryDescription && (
                <Form.Control.Feedback type="invalid">
                  {errors.partyInfo.industryDescription.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CompanyInfoStep;
